import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery, type RtkQueryError } from '@/api/rtkBaseQuery';
import { normalizeAlertSeverity } from '@/utils/alertSeverity';
import type { Alert, Anchor } from '@/types';
import type { AlertEscalationRule } from '@/api/alertEscalationRules';
import type { AxiosError } from 'axios';
import { incidentTypeForAlert } from '@/utils/alertTypes';

/**
 * RTK Query API for the Critical Alerts Console (ADR-004: all NEW server
 * state goes through RTK Query; this is the first adopter).
 *
 * Realtime model: polling. ADR-006 keeps alert-shaped socket events deferred
 * to P4, so the console polls the queue and the selected alert; the
 * escalation clock ticks client-side off createdAt + the facility's
 * escalation-rule delay.
 */

/** Alert detail = list-row shape + the console-only fields the new
 *  GET /facilities/:id/alerts/:alertId endpoint surfaces. Timestamps arrive
 *  as ISO strings through JSON. */
export interface AlertDetail {
  id: string;
  facility_id: string;
  worker_id: string | null;
  person_id?: string;
  /** The worker's name; null when the alert has no worker. */
  person_name?: string | null;
  zone_id?: string;
  zone_name?: string | null;
  alert_type: string;
  severity: Alert['severity'] | string;
  message: string;
  location?: string | null;
  acknowledged: boolean;
  acknowledged_by?: string | null;
  /** The acknowledger's name (first and last, else e-mail); null when unknown. */
  acknowledged_by_name?: string | null;
  acknowledged_at?: string | null;
  resolved: boolean;
  resolved_at?: string | null;
  resolved_by?: string | null;
  resolved_by_name?: string | null;
  /** 'manual' (a person) or 'auto_retention' (closed after the retention period). */
  resolution?: 'manual' | 'auto_retention' | null;
  escalation_level: number;
  last_escalated_at?: string | null;
  /** Whether the last escalation sent an e-mail or SMS; null when not recorded. */
  last_escalation_notified?: boolean | null;
  /** The safety incident reported from this alert, if any. */
  safety_incident_id?: string | null;
  /** When the device says the event happened; null when unknown (the stored time is used). */
  event_at?: string | null;
  /** When the server stored the alert. */
  timestamp: string;
}

/** One decimated bucket; channel triples are [min, max, mean]. */
export interface SensorRecordBucket {
  t_ms: number;
  n: number;
  a: [number, number, number] | null;
  lacc: [number, number, number] | null;
  gyro: [number, number, number] | null;
  tilt: [number, number, number] | null;
}

export interface SensorRecordGap {
  from_ms: number;
  to_ms: number;
}

export interface SensorRecordMetrics {
  peak_impact_g: number | null;
  peak_at_ms: number | null;
  /** The peak reaches 1.5 g; false = the largest value is resting or walking, not an impact. */
  impact_like?: boolean | null;
  free_fall_ms: number | null;
  impact_rise_ms: number | null;
  tilt_before_deg: number | null;
  tilt_after_deg: number | null;
  tilt_delta_deg: number | null;
  motion_resumed_at_ms: number | null;
  still_lacc_g: number | null;
}

export interface SensorRecordBackfill {
  incident_id: string | null;
  /** stalled = in transit with no new fragment for 30 minutes. */
  status: 'none' | 'in_transit' | 'stalled' | 'complete' | 'failed';
  received_fragments: number;
  total_fragments: number;
  progress_pct: number | null;
  time_source: 'alarm_receive_reanchored' | null;
  failure_reason: string | null;
  last_update_at?: string | null;
}

/** What t = 0 of a record is: the device's event time, or the time the server stored the alert. */
export type RecordAnchor = 'device_time' | 'stored_time';

export interface SensorRecord {
  alert_id: string;
  worker_id: string | null;
  sensor: 'neck' | 'waist';
  /** The record's t = 0. */
  event_ts: string;
  anchor?: RecordAnchor;
  stored_ts?: string;
  /** The range the metrics read, whatever the chart shows. */
  metrics_window?: { pre_s: number; post_s: number };
  /** The low-g run is measured below this resultant acceleration (a measurement, not the detector). */
  low_g_threshold_g?: number;
  window: { pre_s: number; post_s: number; bucket_ms: number };
  buckets: SensorRecordBucket[];
  gaps: SensorRecordGap[];
  integrity: {
    expected_samples: number;
    present_samples: number;
    completeness_pct: number;
    utc_valid: boolean | null;
    utc_invalid_count: number;
    device_ids: string[];
    sensors_available: { neck: number; waist: number };
  };
  metrics: SensorRecordMetrics | null;
  backfill?: SensorRecordBackfill;
  reason?: string;
}

export interface CombinedSensorRecordMetrics {
  /** How far apart the two peaks are; comparisons are null when over 2 s (not one impact). */
  peak_gap_ms?: number | null;
  impact_sequence: {
    first: 'neck' | 'waist' | 'same_time';
    delta_ms: number;
  } | null;
  peak_difference_g: number | null;
  tilt_after_spread_deg: number | null;
  motion_resumed_delta_ms: number | null;
}

export interface CombinedSensorRecord {
  alert_id: string;
  worker_id: string | null;
  sensor: 'combined';
  event_ts: string;
  anchor?: RecordAnchor;
  stored_ts?: string;
  metrics_window?: { pre_s: number; post_s: number };
  low_g_threshold_g?: number;
  window: { pre_s: number; post_s: number; bucket_ms: number };
  sensors: { neck: SensorRecord; waist: SensorRecord };
  combined_metrics: CombinedSensorRecordMetrics;
  backfill?: SensorRecordBackfill;
  reason?: string;
}

export interface SensorRecordParams {
  facilityId: string;
  alertId: string;
  sensor?: 'neck' | 'waist';
  preS?: number;
  postS?: number;
  bucketMs?: number;
}

export interface CombinedSensorRecordParams {
  facilityId: string;
  alertId: string;
  preS?: number;
  postS?: number;
  bucketMs?: number;
}

export interface WorkerLocationFix {
  id: string;
  employeeId: string | null;
  x: number;
  y: number;
  floor: number | null;
  zoneId?: string;
  confidence: number | null;
  accuracyMeters: number | null;
  timestamp: string;
}

interface WorkerLocationPage {
  success: boolean;
  data: WorkerLocationFix[];
  meta: { hasMore: boolean; nextCursor: string | null };
}

export interface ReportSafetyIncidentParams {
  facilityId: string;
  alertId: string;
  alertType: string;
  workerId?: string | null;
  severity: string;
  description: string;
  location?: string | null;
  occurredAt: string;
}

/** Open critical and high incidents, and how many there are in all. */
export interface OpenCriticalAlerts {
  alerts: Alert[];
  /** Open critical and high alerts at the facility; more than `alerts` holds when over the page limit. */
  total: number;
}

/** The queue asks for this many of each severity (the list endpoint's maximum). */
const QUEUE_LIMIT = 200;

const normalizeListAlert = (alert: Alert): Alert => {
  const severity = normalizeAlertSeverity(alert.severity);
  return severity ? { ...alert, severity } : alert;
};

export const criticalAlertsApi = createApi({
  reducerPath: 'criticalAlertsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['OpenAlerts', 'AlertDetail'],
  endpoints: (build) => ({
    /** Open (unresolved) critical/high incidents for the queue rail, with the
     *  total (launch-readiness F12-7).
     *
     *  Filtering happens SERVER-SIDE, per severity, with status=open (not
     *  resolved, acknowledged or not): the list endpoint orders newest-first
     *  and applies its limit before any client filter could run, so a single
     *  unfiltered fetch would silently drop an older open critical incident
     *  behind newer low-severity noise. The totals say when more are open than
     *  the queue shows. */
    getOpenCriticalAlerts: build.query<OpenCriticalAlerts, { facilityId: string }>({
      queryFn: async ({ facilityId }) => {
        try {
          const { apiClient } = await import('@/api/client');
          const results = await Promise.all(
            ['critical', 'high'].map((severity) =>
              apiClient.request({
                url: `/facilities/${facilityId}/alerts`,
                method: 'get',
                params: { severity, status: 'open', limit: QUEUE_LIMIT },
              }),
            ),
          );
          const seen = new Set<string>();
          const alerts: Alert[] = [];
          let total = 0;
          for (const res of results) {
            const rows = ((res.data?.data ?? res.data) as Alert[]) ?? [];
            const rawTotal = res.data?.pagination?.total;
            total += typeof rawTotal === 'number' && Number.isFinite(rawTotal) ? rawTotal : rows.length;
            for (const alert of rows) {
              const normalized = normalizeListAlert(alert);
              if (!normalized.resolved && !seen.has(normalized.id)) {
                seen.add(normalized.id);
                alerts.push(normalized);
              }
            }
          }
          alerts.sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
          );
          return { data: { alerts, total: Math.max(total, alerts.length) } };
        } catch (error) {
          const axiosError = error as AxiosError<{ error?: string; message?: string }>;
          const failure: RtkQueryError = {
            status: axiosError.response?.status,
            message: axiosError.response?.data?.error ?? axiosError.response?.data?.message ?? axiosError.message,
          };
          return { error: failure };
        }
      },
      providesTags: [{ type: 'OpenAlerts', id: 'LIST' }],
    }),

    getAlertDetail: build.query<AlertDetail, { facilityId: string; alertId: string }>({
      query: ({ facilityId, alertId }) => ({
        url: `/facilities/${facilityId}/alerts/${alertId}`,
      }),
      transformResponse: (alert: AlertDetail) => ({
        ...alert,
        severity: normalizeAlertSeverity(alert.severity) ?? alert.severity,
      }),
      providesTags: (_result, _error, { alertId }) => [{ type: 'AlertDetail', id: alertId }],
    }),

    getSensorRecord: build.query<SensorRecord, SensorRecordParams>({
      query: ({ facilityId, alertId, sensor, preS, postS, bucketMs }) => ({
        url: `/facilities/${facilityId}/alerts/${alertId}/sensor-record`,
        params: { sensor, preS, postS, bucketMs },
      }),
    }),

    getCombinedSensorRecord: build.query<CombinedSensorRecord, CombinedSensorRecordParams>({
      query: ({ facilityId, alertId, preS, postS, bucketMs }) => ({
        url: `/facilities/${facilityId}/alerts/${alertId}/sensor-record`,
        params: { sensor: 'combined', preS, postS, bucketMs },
      }),
    }),

    getWorkerLocations: build.query<WorkerLocationFix[], {
      facilityId: string; workerId: string; startTime: string; endTime: string;
    }>({
      queryFn: async ({ facilityId, workerId, startTime, endTime }, { signal }) => {
        try {
          const { apiClient } = await import('@/api/client');
          const data: WorkerLocationFix[] = [];
          let cursor: string | undefined;
          do {
            const response = await apiClient.get<WorkerLocationPage>(
              `/facilities/${facilityId}/workers/${workerId}/locations`,
              { params: { startTime, endTime, limit: 500, cursor }, signal },
            );
            data.push(...response.data.data);
            const next = response.data.meta?.hasMore ? response.data.meta.nextCursor : null;
            cursor = next && next !== cursor ? next : undefined;
          } while (cursor);
          return { data };
        } catch (error) {
          const axiosError = error as AxiosError<{ error?: string; message?: string }>;
          return {
            error: {
              status: axiosError.response?.status,
              message:
                axiosError.response?.data?.error ??
                axiosError.response?.data?.message ??
                axiosError.message,
            },
          };
        }
      },
    }),

    getAnchors: build.query<Anchor[], { facilityId: string }>({
      query: ({ facilityId }) => ({ url: `/facilities/${facilityId}/anchors` }),
    }),

    /** Enabled escalation rules: drive the client-side escalation clock. */
    getEscalationRules: build.query<AlertEscalationRule[], { facilityId: string }>({
      query: ({ facilityId }) => ({
        url: `/facilities/${facilityId}/alert-escalation-rules`,
        params: { enabled: 'true' },
      }),
    }),

    acknowledgeAlert: build.mutation<unknown, { facilityId: string; alertId: string }>({
      query: ({ facilityId, alertId }) => ({
        url: `/facilities/${facilityId}/alerts/${alertId}/acknowledge`,
        method: 'post',
        body: {},
      }),
      invalidatesTags: (_result, _error, { alertId }) => [
        { type: 'AlertDetail', id: alertId },
        { type: 'OpenAlerts', id: 'LIST' },
      ],
    }),

    resolveAlert: build.mutation<unknown, { facilityId: string; alertId: string }>({
      query: ({ facilityId, alertId }) => ({
        url: `/facilities/${facilityId}/alerts/${alertId}/resolve`,
        method: 'post',
        body: {},
      }),
      invalidatesTags: (_result, _error, { alertId }) => [
        { type: 'AlertDetail', id: alertId },
        { type: 'OpenAlerts', id: 'LIST' },
      ],
    }),

    /** "Report as safety incident" (launch-readiness F12-3): files one open
     *  incident linked to the alert (safety_incidents.alert_id; the API
     *  answers 409 when the alert was already reported). The type comes from
     *  the alert; recordability is left for the Safety Incidents page. */
    reportSafetyIncident: build.mutation<{ id: string }, ReportSafetyIncidentParams>({
      query: (args) => ({
        url: '/safety-incidents',
        method: 'post',
        body: {
          incident_type: incidentTypeForAlert(args.alertType) ?? 'behavioral',
          occurred_at: args.occurredAt,
          severity: args.severity.toUpperCase(),
          description: args.description.slice(0, 2000),
          facility_id: args.facilityId,
          alert_id: args.alertId,
          // JSON.stringify drops undefined: the .strict() backend schema
          // only ever sees the keys that carry values.
          worker_id: args.workerId ?? undefined,
          location: args.location ? args.location.slice(0, 255) : undefined,
        },
      }),
      invalidatesTags: (_result, _error, { alertId }) => [{ type: 'AlertDetail', id: alertId }],
    }),
  }),
});

export const {
  useGetOpenCriticalAlertsQuery,
  useGetAlertDetailQuery,
  useGetSensorRecordQuery,
  useGetCombinedSensorRecordQuery,
  useGetWorkerLocationsQuery,
  useGetAnchorsQuery,
  useGetEscalationRulesQuery,
  useAcknowledgeAlertMutation,
  useResolveAlertMutation,
  useReportSafetyIncidentMutation,
} = criticalAlertsApi;
